import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plane, Briefcase, PartyPopper, Calendar } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Page, Main, Card, Button, Field, ErrorState, inputClass } from '@/components/ui';
import { useMutation } from '@/hooks/useApi';
import { PLAN_TYPES } from '@/lib/constants';
import { todayISO, cn } from '@/lib/utils';
import type { PlanType } from '@/lib/types';

const TYPE_ICONS: Record<PlanType, typeof Calendar> = {
  vacation: Plane,
  business: Briefcase,
  event: PartyPopper,
  other: Calendar,
};

export function PlannerNewPage() {
  const navigate = useNavigate();
  const { mutate, loading, error } = useMutation();

  const [name, setName] = useState('');
  const [planType, setPlanType] = useState<PlanType>('vacation');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(todayISO());

  const datesValid = startDate !== '' && endDate !== '' && startDate <= endDate;
  const canSave = name.trim() !== '' && datesValid && !loading;

  const handleSave = async () => {
    if (!canSave) return;
    const result = await mutate('post', '/plans', {
      name: name.trim(),
      planType,
      startDate,
      endDate,
    });
    const id = result?.plan?.id as string | undefined;
    if (id) {
      navigate(`/fits/planner/${id}`);
    }
  };

  return (
    <Page>
      <PageHeader section="New Plan" backHref="/fits/planner" backLabel="Planner" />
      <Main className="space-y-5">
        {error && <ErrorState message={error} />}

        <Card className="p-4 space-y-4">
          <h2 className="text-sm font-semibold text-foreground">Plan Details</h2>

          <Field label="Plan Name">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Miami Beach Vacation"
              className={inputClass}
            />
          </Field>

          <Field label="Plan Type">
            <div className="flex gap-2">
              {PLAN_TYPES.map((t) => {
                const Icon = TYPE_ICONS[t.id];
                const active = planType === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setPlanType(t.id)}
                    className={cn(
                      'flex-1 flex flex-col items-center gap-1.5 p-3 rounded-xl border transition-all',
                      active
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-secondary border-border hover:border-primary/30',
                    )}
                  >
                    <Icon className={cn('w-5 h-5', active ? 'text-primary-foreground' : 'text-muted-foreground')} />
                    <span
                      className={cn(
                        'text-[10px] font-medium',
                        active ? 'text-primary-foreground' : 'text-muted-foreground',
                      )}
                    >
                      {t.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Start Date">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={`${inputClass} w-full min-w-0`}
              />
            </Field>
            <Field label="End Date">
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={`${inputClass} w-full min-w-0`}
              />
            </Field>
          </div>

          {!datesValid && (startDate || endDate) && (
            <p className="text-xs text-destructive">End date must be on or after the start date.</p>
          )}
        </Card>

        <Button fullWidth onClick={handleSave} disabled={!canSave}>
          {loading ? 'Creating…' : 'Create Plan'}
        </Button>
        <p className="text-center text-xs text-muted-foreground">
          You'll add fits to each day on the next screen.
        </p>
      </Main>
    </Page>
  );
}
